import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { CameraIcon as Camera, TrashIcon as Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import { thumbUrl } from "@/lib/utils-app";

/**
 * Inline kennel photo / logo uploader for the kennel header.
 *
 * Upload flow:
 *   1. user picks a file → POST to `/api/storage/upload` (existing
 *      endpoint handles size + thumbnail generation)
 *   2. PUT the resulting URL to `/api/breeders/{id}/photo` so the
 *      breeder doc persists `photo_url`
 *   3. caller's `onSaved` patches local state so the UI re-renders
 *      without a round-trip
 *
 * Only the kennel owner (or admin) can change the photo — the
 * backend route enforces this, and the UI hides the camera CTA for
 * read-only viewers via the `editable` prop.
 */
export default function KennelPhotoHeader({ breeder, editable, onSaved }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const photoUrl = breeder?.photo_url || breeder?.logo_url || null;

  const onPick = () => fileRef.current?.click();

  const onChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // allow re-picking the same file
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const uploadRes = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = uploadRes.data?.url || uploadRes.data?.storage_path;
      if (!url) throw new Error("Upload did not return a URL.");
      const saveRes = await api.put(`/breeders/${breeder.id}/photo`, {
        photo_url: url,
      });
      onSaved?.({ photo_url: saveRes.data?.photo_url || url });
      toast.success("Kennel photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update photo");
    } finally {
      setBusy(false);
    }
  };

  const onClear = async () => {
    if (!photoUrl) return;
    if (!window.confirm("Remove your kennel photo?")) return;
    setBusy(true);
    try {
      await api.put(`/breeders/${breeder.id}/photo`, { photo_url: null });
      onSaved?.({ photo_url: null });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not clear photo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="relative h-24 w-24 md:h-28 md:w-28 rounded-full flex items-center justify-center flex-shrink-0 group"
      data-testid="kennel-photo-header"
    >
      <div className="absolute inset-0 rounded-full overflow-hidden ring-1 ring-gold/40 bg-cream flex items-center justify-center">
        {photoUrl ? (
          <img
            src={thumbUrl(photoUrl)}
            alt={breeder?.kennel_name || breeder?.name || "Kennel photo"}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-3xl font-spectral text-[#8A6A00] select-none">
            {(breeder?.kennel_name || breeder?.name || "?").trim().charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      {editable && (
        <>
          <button
            type="button"
            onClick={onPick}
            disabled={busy}
            className="absolute bottom-0.5 right-0.5 h-8 w-8 rounded-full bg-ink text-white flex items-center justify-center shadow-md hover:bg-crimson transition-colors disabled:opacity-60 disabled:cursor-wait"
            aria-label="Change kennel logo"
            title="Change kennel logo"
            data-testid="kennel-photo-upload-btn"
          >
            <Camera size={15} weight="duotone" />
          </button>
          {photoUrl && (
            <button
              type="button"
              onClick={onClear}
              disabled={busy}
              className="absolute top-0.5 right-0.5 h-7 w-7 rounded-full bg-white border border-[#E5E5E5] flex items-center justify-center text-[#C1272D] hover:bg-[#FFF0F0] shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove kennel logo"
              data-testid="kennel-photo-clear-btn"
              title="Remove logo"
            >
              <Trash size={12} weight="bold" />
            </button>
          )}
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onChange}
            className="hidden"
            data-testid="kennel-photo-file-input"
          />
        </>
      )}
    </div>
  );
}
